import { defineStore } from 'pinia';
import { computed, ref } from 'vue';
import { autoAuctionApiSdk } from '../sdk/autoAuctionClient.js';
import { idOf } from '../sdk/identity.js';
import { useRealtime } from '../adapters/ports.js';
import { sessionStore } from '@hds/identity-client-sdk';

function myId() {
  const me = sessionStore().user;
  if (!me?._id) return null;
  return typeof me._id === 'string' ? me._id : me._id.$oid ?? null;
}

function minutesSince(iso) {
  if (!iso) return Number.POSITIVE_INFINITY;
  const t = new Date(iso).getTime();
  if (!Number.isFinite(t)) return Number.POSITIVE_INFINITY;
  return Math.max(0, (Date.now() - t) / 60000);
}

// The client half of vibe-server's one archive predicate (`archivedFor` in
// ConversationsRoutes.mjs). Archive is per participant; a thread archived before
// that field existed carries only the thread-wide legacy `archived_at`, which
// counts for every participant who has not said otherwise. Saying otherwise is an
// explicit `false` entry, which is why unarchive writes `false` instead of
// dropping the key.
function archivedFor(doc, me) {
  const entry = (doc?.archivedByParticipantId || {})[me];
  if (typeof entry === 'boolean') return entry;
  return !!doc?.archived_at;
}

function normalizeConversation(doc) {
  if (!doc) return null;
  const id = idOf(doc);
  if (!id) return null;
  const me = myId();
  const unreadMap = doc.unreadByParticipantId || {};
  const unreadCount = me ? Number(unreadMap[me] || 0) : 0;
  const mutedMap = doc.mutedByParticipantId || {};
  const muted = me ? !!mutedMap[me] : false;
  return {
    id,
    dealerId: doc.dealerId ?? null,
    category: doc.category ?? 'buying',
    minutesAgo: minutesSince(doc.lastMessageAt),
    lastMessageAt: doc.lastMessageAt ?? null,
    unreadCount,
    // Phase B — server stores `isTyping` as the userId of whoever is
    // typing (or null). Coerce anything else (e.g. legacy boolean
    // values from older seed data) to null, and drop self-typing
    // outright so an echo from our own WS frame can't paint the pip
    // back on our own inbox row.
    isTyping:
      typeof doc.isTyping === 'string' && doc.isTyping && doc.isTyping !== me
        ? doc.isTyping
        : null,
    pinnedVehicleId: doc.pinnedVehicleId ?? null,
    preview: doc.preview ?? { kind: 'text', body: '' },
    attachedVehicleId: doc.attachedVehicleId ?? null,
    participantIds: Array.isArray(doc.participantIds) ? doc.participantIds : [],
    participantProfiles: Array.isArray(doc.participantProfiles) ? doc.participantProfiles : [],
    leadId: doc.leadId ?? null,
    muted,
    archived: me ? archivedFor(doc, me) : !!doc.archived_at
  };
}

// Resolve the counterparty for a conversation from its denormalized
// `participantProfiles` — the entry whose id isn't the viewer's. Reduced
// scope: we no longer fetch the other human's doc (cross-company 403) nor
// branch on dealer/consumer role. Falls back to the first profile (then a
// generic label) so a row always has a name.
export function counterpartyOf(
  conv
) {
  const me = myId();
  const profiles = Array.isArray(conv?.participantProfiles) ? conv.participantProfiles : [];
  const other = profiles.find((p) => String(p.id) !== String(me)) ?? profiles[0] ?? null;
  return {
    id: other?.id ?? null,
    name: other?.name || 'Participant',
    company: other?.company || ''
  };
}

function normalizeMessage(doc) {
  if (!doc) return null;
  const id = idOf(doc);
  if (!id) return null;
  const me = myId();
  const authorIsMe = me && String(doc.authorId) === me;
  const sent = doc.sentAt ? new Date(doc.sentAt) : null;
  const timeLabel = sent
    ? `${sent.getHours()}:${String(sent.getMinutes()).padStart(2, '0')}`
    : '';
  const day = sent ? dayLabelFor(sent) : '';
  return {
    id,
    author: authorIsMe ? 'me' : 'them',
    kind: doc.kind ?? 'text',
    body: doc.body ?? null,
    vehicleId: doc.vehicleId ?? null,
    imageUrl: doc.imageUrl ?? null,
    attachments: Array.isArray(doc.attachments) ? doc.attachments : [],
    // Structured Phase C sub-payloads — null for non-matching kinds.
    offer: doc.offer ?? null,
    system: doc.system ?? null,
    sentAt: doc.sentAt ?? null,
    timeLabel,
    day,
    readReceipt: Array.isArray(doc.readBy) && doc.readBy.length > 0
  };
}

// The count "mark as unread" restores, read off the cached thread: the run of
// messages from the other participant at the end of it — what the dealer still
// owes a reply to. The same rule the server applies on PATCH, so the optimistic
// badge and the row the server hands back agree instead of flickering from 1 to
// the real number. Answers null when the thread was never loaded, which is the
// caller's cue to fall back.
function unansweredCountOf(messages) {
  if (!Array.isArray(messages)) return null;
  let count = 0;
  for (let i = messages.length - 1; i >= 0; i -= 1) {
    if (messages[i].author === 'me') break;
    count += 1;
  }
  return count;
}

// Coarse day bucket the conversation page uses to group consecutive
// messages. "Today" / "Yesterday" / weekday names — same vocabulary
// the design comp showed.
function dayLabelFor(date) {
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const target = new Date(date.getFullYear(), date.getMonth(), date.getDate());
  const diffDays = Math.round((today.getTime() - target.getTime()) / 86400000);
  if (diffDays <= 0) return 'Today';
  if (diffDays === 1) return 'Yesterday';
  if (diffDays < 7) {
    const days = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
    return days[date.getDay()];
  }
  return 'Earlier';
}

// Convert a minutes-ago offset to the compact label the row shows.
export function relativeLabel(minutes) {
  if (!Number.isFinite(minutes)) return '';
  if (minutes < 60) return `${Math.max(1, Math.round(minutes))}m`;
  if (minutes < 60 * 24) return `${Math.round(minutes / 60)}h`;
  const days = Math.round(minutes / (60 * 24));
  if (days === 1) return 'Yesterday';
  if (days < 7) {
    const daysOfWeek = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
    const idx = (new Date().getDay() - days + 7) % 7;
    return daysOfWeek[idx];
  }
  return `${days}d`;
}

export const messagesStore = defineStore('messagesStore', () => {
  const conversations = ref([]);
  const threads = ref({});
  // Humans the compose flow may start a thread with. The server scopes this
  // list — an auction-house employee gets dealership humans, everyone else
  // gets nothing — so the client renders it verbatim and never role-checks.
  const recipients = ref([]);
  const recipientsLoading = ref(false);
  const activeFilter = ref('all');
  const searchQuery = ref('');
  const loading = ref(false);
  const error = ref(null);
  const loaded = ref(false);
  // Per-conversation loading state for individual threads — keyed by id.
  const threadLoading = ref({});
  // Phase B — id of the conversation currently rendered on
  // ConversationPage; the WS message.created handler uses this to
  // decide between "append-and-auto-read" vs "insert-and-bump-unread".
  const activeConversationId = ref(null);

  function setActiveConversation(id) {
    activeConversationId.value = id ?? null;
  }

  // `archived` is a view, not a slice of the inbox: every other filter hides
  // archived rows and this one shows nothing else. The page labels these from its
  // own catalog; the labels here are the fallback.
  const filterOptions = [
    { key: 'all', label: 'All' },
    { key: 'unread', label: 'Unread' },
    { key: 'archived', label: 'Archived' },
  ];

  async function loadConversations({ force = false } = {}) {
    if (loaded.value && !force) return;
    loading.value = true;
    error.value = null;
    try {
      // Both halves, in one go. The server's two lists are disjoint and the
      // archived one is small, so fetching it with the inbox costs one request
      // and buys an Archived view that is populated the moment it is tapped —
      // and pills that are drawn even for a dealer whose every thread is
      // archived, since `conversations` is then not empty.
      const [open, archived] = await Promise.all([
        autoAuctionApiSdk.getConversations(),
        autoAuctionApiSdk.getConversations({ archived: true })
      ]);
      const byId = new Map();
      for (const doc of [...open, ...archived]) {
        const normalized = normalizeConversation(doc);
        if (normalized) byId.set(normalized.id, normalized);
      }
      conversations.value = [...byId.values()];
      loaded.value = true;
    } catch (e) {
      error.value = e;
      console.error('Failed to load conversations', e);
    } finally {
      loading.value = false;
    }
  }

  // Deliberately NOT cached between calls. `session.logout()` is a router.push
  // with no reload, so this store outlives a sign-out — a cached list would
  // hand the next user the previous user's recipients, and since the compose
  // button keys off `recipients.length`, the previous user's compose button
  // along with them. Refetching per inbox mount is one small request; a stale
  // answer here is an identity leak. Same reason the catch clears rather than
  // leaves the old list standing.
  async function loadRecipients() {
    recipientsLoading.value = true;
    try {
      recipients.value = await autoAuctionApiSdk.getConversationRecipients();
    } catch (e) {
      console.error('Failed to load conversation recipients', e);
      recipients.value = [];
    } finally {
      recipientsLoading.value = false;
    }
  }

  async function loadThread(conversationId, { force = false } = {}) {
    if (!conversationId) return;
    if (threads.value[conversationId] && !force) return;
    threadLoading.value = { ...threadLoading.value, [conversationId]: true };
    try {
      const documents = await autoAuctionApiSdk.getMessages(conversationId);
      threads.value = {
        ...threads.value,
        [conversationId]: documents.map(normalizeMessage).filter(Boolean)
      };
    } catch (e) {
      console.error('Failed to load thread', e);
    } finally {
      threadLoading.value = { ...threadLoading.value, [conversationId]: false };
    }
  }

  // Archived rows are out of every count: an archived thread is one the dealer
  // has put away, and a new message unarchives it, so a badge for one would point
  // at a row no filter but Archived draws.
  const totalUnread = computed(() =>
    conversations.value.reduce((sum, c) => (c.archived ? sum : sum + c.unreadCount), 0)
  );

  const filteredConversations = computed(() => {
    const list = conversations.value.slice().sort((a, b) => a.minutesAgo - b.minutesAgo);
    if (activeFilter.value === 'archived') return list.filter((c) => c.archived);
    // Archive is checked HERE rather than only on the next fetch, so an archived
    // row leaves the inbox while `updateConversation` is still in flight.
    const open = list.filter((c) => !c.archived);
    if (activeFilter.value === 'all') return open;
    if (activeFilter.value === 'unread') return open.filter((c) => c.unreadCount > 0);
    return open.filter((c) => c.category === activeFilter.value);
  });

  const unreadCountFor = (key) => {
    if (key === 'unread') return totalUnread.value;
    if (key === 'all') return totalUnread.value;
    // The Archived pill carries no count. It is a view of what the dealer put
    // away, not a queue to work through.
    if (key === 'archived') return 0;
    return conversations.value
      .filter((c) => !c.archived && c.category === key)
      .reduce((sum, c) => sum + c.unreadCount, 0);
  };

  const setFilter = (key) => {
    if (filterOptions.some((f) => f.key === key)) activeFilter.value = key;
  };

  const getConversation = (id) =>
    conversations.value.find((c) => c.id === id) ?? null;

  const getThread = (id) => threads.value[id] ?? [];

  // Hydrate the inbox + every thread's messages. loadConversations /
  // loadThread are both idempotent, so re-calling this on every visit only
  // fetches the gaps.
  async function ensureAllThreadsLoaded({ force = false } = {}) {
    await loadConversations({ force });
    await Promise.all(
      conversations.value.map((c) => loadThread(c.id, { force }))
    );
  }

  const markRead = async (id) => {
    const c = conversations.value.find((x) => x.id === id);
    if (!c) return;
    const previous = c.unreadCount;
    c.unreadCount = 0;
    try {
      await autoAuctionApiSdk.markConversationRead(id);
    } catch (e) {
      c.unreadCount = previous;
      console.error('Failed to mark conversation read', e);
    }
  };

  // Optimistically append a message, then call the SDK. The composer
  // can pass either plain text, an image attachment, a vehicle attachment,
  // or any combination via the `attachments` array — the optimistic
  // bubble mirrors whichever shape we just persisted.
  const sendMessage = async (conversationId, bodyOrPayload, options = {}) => {
    // Backwards compatible signature: callers used to pass `(id, "text")`.
    // Newer callers pass `(id, { body, kind, attachments, vehicleId, imageUrl })`.
    const payload =
      typeof bodyOrPayload === 'string'
        ? { kind: 'text', body: bodyOrPayload, ...options }
        : { kind: 'text', ...bodyOrPayload };

    const text = typeof payload.body === 'string' ? payload.body.trim() : '';
    payload.body = text || null;

    // Pick the primary kind for the optimistic bubble. An attachment-only
    // message still routes as 'image' or 'vehicle' so the bubble renders
    // the right preview.
    const primaryAttachment = Array.isArray(payload.attachments)
      ? payload.attachments[0]
      : null;
    if (!payload.kind || payload.kind === 'text') {
      if (primaryAttachment?.kind === 'image') payload.kind = 'image';
      else if (primaryAttachment?.kind === 'vehicle') payload.kind = 'vehicle';
      // NOT renamed with the rest of the feature: 'wishlist' is an attachment
      // kind on the wire, read off server payloads and stored in
      // `conversations`. Changing the client half only stops it matching, so
      // the value stays until the messages themselves are migrated — which
      // belongs with the sharing cleanup, since sharing is the cut feature that
      // produced these.
      else if (primaryAttachment?.kind === 'wishlist') payload.kind = 'wishlist';
    }
    if (primaryAttachment?.kind === 'vehicle' && !payload.vehicleId) {
      payload.vehicleId = primaryAttachment.vehicleId ?? null;
    }
    if (primaryAttachment?.kind === 'image' && !payload.imageUrl) {
      payload.imageUrl = primaryAttachment.url ?? null;
    }

    // Structured kinds (offer) carry their payload in a dedicated field,
    // not in `body` / attachments — let them through.
    const hasStructuredPayload = payload.kind === 'offer' && payload.offer;
    if (!text && !primaryAttachment && !hasStructuredPayload) return null;

    const now = new Date();
    const tentativeId = `tmp-${Date.now()}`;
    const optimisticOffer =
      payload.kind === 'offer' && payload.offer
        ? { status: 'pending', currency: 'USD', ...payload.offer }
        : null;
    const optimistic = {
      id: tentativeId,
      author: 'me',
      kind: payload.kind || 'text',
      body: text || null,
      vehicleId: payload.vehicleId ?? null,
      imageUrl: payload.imageUrl ?? null,
      attachments: Array.isArray(payload.attachments) ? payload.attachments : [],
      offer: optimisticOffer,
      system: null,
      sentAt: now.toISOString(),
      timeLabel: `${now.getHours()}:${String(now.getMinutes()).padStart(2, '0')}`,
      day: 'Today',
      readReceipt: false
    };
    const existing = threads.value[conversationId] ?? [];
    threads.value = { ...threads.value, [conversationId]: [...existing, optimistic] };

    const conv = conversations.value.find((c) => c.id === conversationId);
    const previousPreview = conv?.preview;
    const previousMinutesAgo = conv?.minutesAgo;
    if (conv) {
      conv.preview = previewFromPayload(payload, text);
      conv.minutesAgo = 0.5;
      conv.lastMessageAt = now.toISOString();
    }

    try {
      const saved = await autoAuctionApiSdk.sendMessage(conversationId, {
        kind: payload.kind || 'text',
        body: text || null,
        vehicleId: payload.vehicleId ?? null,
        imageUrl: payload.imageUrl ?? null,
        attachments: payload.attachments ?? null,
        offer: payload.offer ?? null
      });
      const confirmed = normalizeMessage(saved);
      threads.value = {
        ...threads.value,
        [conversationId]: (threads.value[conversationId] || []).map((m) =>
          m.id === tentativeId ? confirmed : m
        )
      };
      return confirmed;
    } catch (e) {
      // Roll back the optimistic update so the composer can re-prompt.
      threads.value = {
        ...threads.value,
        [conversationId]: (threads.value[conversationId] || []).filter(
          (m) => m.id !== tentativeId
        )
      };
      if (conv) {
        conv.preview = previousPreview;
        conv.minutesAgo = previousMinutesAgo;
      }
      console.error('Failed to send message', e);
      return null;
    }
  };

  function previewFromPayload(payload, text) {
    if (payload.kind === 'image') return { kind: 'photo-sent', body: text || 'Photo' };
    if (payload.kind === 'vehicle') return { kind: 'text', body: text || 'Shared a vehicle' };
    if (payload.kind === 'wishlist') return { kind: 'text', body: text || 'Shared a wishlist' };
    if (payload.kind === 'offer') {
      const amount = Number(payload.offer?.amount);
      const formatted = Number.isFinite(amount) ? `$${amount.toLocaleString('en-US')}` : '';
      return { kind: 'text', body: formatted ? `Offer: ${formatted}` : 'Offer' };
    }
    return { kind: 'text', body: text };
  }

  const setSearchQuery = (q) => {
    searchQuery.value = typeof q === 'string' ? q : '';
  };

  // Create a conversation between the caller and one other participant, and
  // insert the returned doc at the top of the inbox so the navigator can jump
  // straight into it. Takes the counterparty alone and derives the caller: the
  // server requires exactly two distinct participants with the caller as one of
  // them, so accepting a raw `participantIds` array would only let callers build
  // pairs the server has to reject.
  const createConversation = async (recipientId) => {
    const me = myId();
    if (!me || !recipientId) return null;
    if (String(recipientId) === me) return null;
    try {
      const saved = await autoAuctionApiSdk.createConversation({
        participantIds: [me, String(recipientId)]
      });
      const normalized = normalizeConversation(saved);
      if (normalized) {
        // The server dedupes by participant pair and hands back the existing
        // thread, so a row for this id may well be in the inbox already.
        const existingIdx = conversations.value.findIndex((c) => c.id === normalized.id);
        if (existingIdx >= 0) conversations.value.splice(existingIdx, 1, normalized);
        else conversations.value.unshift(normalized);
      }
      return normalized;
    } catch (e) {
      console.error('Failed to create conversation', e);
      return null;
    }
  };

  // Patch flags on a conversation (mute / archive / mark unread). The
  // optimistic update mirrors the patch on the local row so the inbox
  // re-renders before the request resolves.
  const updateConversation = async (id, patch) => {
    const conv = conversations.value.find((c) => c.id === id);
    if (!conv) return null;
    const previous = {
      muted: conv.muted,
      archived: conv.archived,
      unreadCount: conv.unreadCount
    };
    if (typeof patch.muted === 'boolean') conv.muted = patch.muted;
    if (typeof patch.archived === 'boolean') conv.archived = patch.archived;
    if (patch.readState === 'unread') {
      // The thread page loads the thread before its menu can open, so the cached
      // run is the normal path; an uncached row keeps the old floor-of-one guess.
      const run = unansweredCountOf(threads.value[id]);
      conv.unreadCount = Math.max(1, run ?? conv.unreadCount ?? 0);
    }
    if (patch.readState === 'read') conv.unreadCount = 0;

    try {
      const saved = await autoAuctionApiSdk.updateConversation(id, patch);
      const normalized = normalizeConversation(saved);
      if (normalized) {
        const idx = conversations.value.findIndex((c) => c.id === id);
        if (idx >= 0) conversations.value.splice(idx, 1, normalized);
      }
      return normalized;
    } catch (e) {
      conv.muted = previous.muted;
      conv.archived = previous.archived;
      conv.unreadCount = previous.unreadCount;
      console.error('Failed to update conversation', e);
      return null;
    }
  };

  // Phase B WS — merge a server-pushed conversation doc into the local
  // cache. Treats unknown ids as inserts so a brand-new conversation
  // started on another device shows up without a full reload.
  function mergeConversation(rawDoc) {
    const normalized = normalizeConversation(rawDoc);
    if (!normalized) return null;
    const idx = conversations.value.findIndex((c) => c.id === normalized.id);
    if (idx >= 0) {
      // Preserve fields the row carried locally that the server doesn't
      // own (e.g., optimistic preview / minutesAgo). The server-derived
      // unreadCount/typing/leadId/etc. overrides whatever was stale.
      const prev = conversations.value[idx];
      conversations.value.splice(idx, 1, { ...prev, ...normalized });
    } else {
      conversations.value.unshift(normalized);
    }
    return normalized;
  }

  // Phase B WS — append a server-pushed message to the matching thread
  // when we already have it loaded. Threads we haven't opened yet get a
  // no-op; they'll hydrate fresh on first visit and pick up the message.
  function appendIncomingMessage(conversationId, messageDoc) {
    if (!conversationId || !messageDoc) return;
    const existing = threads.value[conversationId];
    if (!existing) return; // Not loaded — nothing to append onto.
    const normalized = normalizeMessage(messageDoc);
    if (!normalized) return;
    // Guard against the rare echo where the sender's own optimistic
    // bubble has already been confirmed by the POST response.
    if (existing.some((m) => m.id === normalized.id)) return;
    threads.value = {
      ...threads.value,
      [conversationId]: [...existing, normalized]
    };
  }

  // Phase B WS subscribers — wired once when the store is instantiated
  // (Pinia caches the setup). The realtime singleton fans out frames
  // unconditionally; we filter on event type and conversation membership.
  useRealtime().subscribe('message.created', (frame) => {
    const conv = frame?.conversation;
    const msg = frame?.message;
    if (!conv || !msg) return;
    mergeConversation(conv);
    const convId = idOf(conv);
    if (!convId) return;
    if (activeConversationId.value === convId) {
      appendIncomingMessage(convId, msg);
      // Active thread is on-screen — sync read state both directions so
      // the sender's check-mark turns green without waiting on the next
      // user interaction.
      const local = conversations.value.find((c) => c.id === convId);
      if (local) local.unreadCount = 0;
      useRealtime().send({ type: 'conversation.read', conversationId: convId });
    } else {
      // Background thread — leave the unread count the server attached
      // via mergeConversation. If the thread happens to be cached
      // already, drop the message in so the next open is up to date.
      appendIncomingMessage(convId, msg);
    }
  });

  useRealtime().subscribe('conversation.updated', (frame) => {
    if (!frame?.conversation) return;
    mergeConversation(frame.conversation);
  });

  return {
    conversations,
    threads,
    recipients,
    recipientsLoading,
    activeFilter,
    searchQuery,
    filterOptions,
    loading,
    error,
    loaded,
    threadLoading,
    activeConversationId,
    setActiveConversation,
    loadConversations,
    loadRecipients,
    loadThread,
    ensureAllThreadsLoaded,
    totalUnread,
    filteredConversations,
    unreadCountFor,
    setFilter,
    setSearchQuery,
    getConversation,
    getThread,
    markRead,
    sendMessage,
    createConversation,
    updateConversation
  };
});
